import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import MapTooltip from '../map-tooltip';
import { setActiveRegion } from '../../actions/ui';
import bboxArray from 'turf-bbox';
import * as selectors from '../../reducers';
import WebMercatorViewport from 'viewport-mercator-project';
import ReactMapGL, {
  SVGOverlay,
  FlyToInterpolator,
  Marker,
} from 'react-map-gl';
import { parse as SvgParser } from 'svg-parser';
import geojson2svg from 'geojson-to-svg';
import useComponentSize from '@rehooks/component-size';
import styles from './map.module.scss';
import { PlusIcon, MinusIcon } from '../icons';

const COLOR_BLUE = 'rgb(200, 204, 230)';
const COLOR_HIGHLIGHT = '#6a74c4';
const DEFAULT_COORDINATES = [-19.5, 146];
const minZoom = 3.895055500278351;

const initialViewport = {
  latitude: DEFAULT_COORDINATES[0],
  longitude: DEFAULT_COORDINATES[1],
  zoom: 4.7,
  width: 200,
  height: 600,
};

const getPathObjects = ({ project, feature }) => {
  const svgString = geojson2svg().projection(project).data(feature).render();
  const svgObj = SvgParser(svgString);
  const gObj = svgObj.children[0];
  const pathObjs = gObj.children;
  return pathObjs;
};

const flyTo = {
  transitionDuration: 700,
  transitionInterpolator: new FlyToInterpolator(),
};

function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

function Pin(props) {
  const { size = 20, active, onClick, onMouseEnter, onMouseLeave } = props;
  const DamMarker = (
    <path
      d="M11.3722 6.00016C11.3722 8.64653 6.14409 13.6668 6.14409 13.6668C6.14409 13.6668 0.916016 8.64653 0.916016 6.00016C0.916016 3.3538 3.2567 1.2085 6.14409 1.2085C9.03147 1.2085 11.3722 3.3538 11.3722 6.00016Z"
      fill="#E98D67"
      stroke="white"
    />
  );
  const ActiveDamMarker = (
    <path
      d="M11.4737 5.58317C11.4737 8.22954 6.24565 13.2498 6.24565 13.2498C6.24565 13.2498 1.01758 8.22954 1.01758 5.58317C1.01758 2.93681 3.35826 0.791504 6.24565 0.791504C9.13303 0.791504 11.4737 2.93681 11.4737 5.58317Z"
      fill="white"
      stroke="#E98D67"
    />
  );

  const pinStyle = {
    cursor: 'pointer',
    fill: '#d00',
    stroke: 'none',
  };

  return (
    <svg
      height={size}
      viewBox="0 0 12 15"
      style={{ ...pinStyle, transform: `translate(${-size / 2}px,${-size}px)` }}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {active ? ActiveDamMarker : DamMarker}
    </svg>
  );
}

const Map = ({
  qldCouncilsGeo,
  council,
  isMobile,
  onCouncilSelect,
  scrollZoom,
  regions,
  dams,
  onDamSelect,
  activeDam,
}) => {
  const [viewport, setViewport] = useState(initialViewport);
  const [hoveredCouncil, setHoveredCouncil] = useState(null);
  const [hoveredDam, setHoveredDam] = useState(null);
  const [isPanning, setIsPanning] = useState(false);
  const prevIsPanning = usePrevious(isPanning);

  let mapContainerRef = useRef(null);
  let { width, height } = useComponentSize(mapContainerRef);

  const getCouncilBounds = () => {
    const geoCouncil = qldCouncilsGeo.features.find((councilGeo) => {
      return councilGeo.properties['LGA_NAME_2016'] === council.lgaName;
    });
    const boundsArray = bboxArray(geoCouncil.geometry);
    return [
      [boundsArray[0], boundsArray[1]],
      [boundsArray[2], boundsArray[3]],
    ];
  };

  const getQldBounds = () => {
    const BBOX = [
      137.994324998,
      -29.1778726869999,
      153.552171248,
      -9.21993695299994,
    ];
    const boundsArray = BBOX;
    return [
      [boundsArray[0], boundsArray[1]],
      [boundsArray[2], boundsArray[3]],
    ];
  };

  //   When the council changes, set map bounds to the new council
  useEffect(() => {
    if (!width) return;
    // bbox for the region or QLD, depending on whether there's an active region
    const bounds = council ? getCouncilBounds() : getQldBounds();
    const padding = council ? 0.2 : 0;
    const flying = council ? flyTo : {};
    const newViewport = new WebMercatorViewport({
      width: width,
      height: height,
    }).fitBounds(bounds);
    setViewport({
      ...newViewport,
      zoom: newViewport.zoom - padding,
      ...flying,
    });
  }, [council, width, height]);

  return (
    <div
      ref={mapContainerRef}
      style={{ width: '100%', height: '100%', cursor: 'grab' }}
    >
      <ReactMapGL
        scrollZoom={scrollZoom}
        dragPan={true}
        dragRotate={false}
        doubleClickZoom={false}
        touchZoom={isMobile}
        touchRotate={false}
        onViewportChange={(viewport) => setViewport(viewport)}
        initialViewState={initialViewport}
        viewState={viewport}
        width={'100%'}
        onInteractionStateChange={(s) => setIsPanning(s.isDragging)}
        height={'100%'}
        mapStyle={{
          version: 8,
          name: 'Basic',
          sources: {
            neighbours: {
              type: 'geojson',
              data: `${process.env.PUBLIC_URL}/data/neighbours.geo.json`,
            },
          },
          layers: [
            {
              id: 'neighbour-layer',
              type: 'fill',
              source: 'neighbours',
              paint: {
                'fill-color': 'rgb(235, 235, 246)',
              },
            },
          ],
        }}
      >
        <SVGOverlay
          redraw={({ project }) => {
            if (!qldCouncilsGeo) return null;
            const nameSelector = (x) => x.properties.LGA_NAME_2016;
            return qldCouncilsGeo.features.map((x, i) => {
              const pathObjs = getPathObjects({ project, feature: x });
              const active =
                nameSelector(x) === hoveredCouncil ||
                (council && nameSelector(x) === council.lgaName);
              return (
                <g key={nameSelector(x)}>
                  {pathObjs.map((path, i) => {
                    return (
                      <path
                        onClick={() => {
                          if (!active) {
                            setHoveredCouncil(nameSelector(x));
                          } else {
                            // hack to not execute a click if the panning just ended
                            if (!prevIsPanning) {
                              onCouncilSelect(
                                regions.find(
                                  (r) => r.lgaName === nameSelector(x)
                                ).name
                              );
                            }
                          }
                        }}
                        onMouseEnter={(e) => {
                          if (isMobile) return;
                          setHoveredCouncil(nameSelector(x));
                        }}
                        onMouseLeave={() => setHoveredCouncil(null)}
                        key={'fill' + i}
                        stroke="white"
                        strokeWidth="1"
                        fill={active ? COLOR_HIGHLIGHT : COLOR_BLUE}
                        style={{
                          cursor: 'pointer',
                        }}
                        d={path.attributes.d}
                      />
                    );
                  })}
                </g>
              );
            });
          }}
        />
        {dams.map((x) => {
          return (
            <Marker
              key={x.dam}
              latitude={x.locationLatitude}
              longitude={x.locationLongitude}
            >
              <Pin
                active={activeDam && activeDam.dam === x.dam}
                onMouseEnter={() => setHoveredDam(x.dam)}
                onMouseLeave={() => setHoveredDam(null)}
                onClick={() => onDamSelect(x.dam)}
              />
            </Marker>
          );
        })}
      </ReactMapGL>

      <MapTooltip
        visible={Boolean(hoveredCouncil) && !hoveredDam}
        position={{ x: 0, y: 0 }}
        isStatic={true}
      >
        {hoveredCouncil}
      </MapTooltip>
      <MapTooltip
        visible={Boolean(hoveredDam)}
        position={{ x: 0, y: 0 }}
        isStatic={true}
      >
        {hoveredDam}
      </MapTooltip>
      <div
        className={styles.navigationControls}
        style={{ position: 'absolute', right: 10, top: 10 }}
      >
        <div>
          <div className="mapboxgl-ctrl mapboxgl-ctrl-group ">
            <button
              onClick={() => {
                const newZoom = viewport.zoom + 1;
                setViewport({ ...viewport, ...flyTo, zoom: newZoom });
              }}
              className="mapboxgl-ctrl-icon mapboxgl-ctrl-zoom-in"
              type="button"
              title="Zoom In"
            >
              <PlusIcon width={12} height={12} />
            </button>
            <button
              onClick={() => {
                const newZoom = viewport.zoom - 1;
                if (newZoom < minZoom) return;
                setViewport({ ...viewport, ...flyTo, zoom: newZoom });
              }}
              className="mapboxgl-ctrl-icon mapboxgl-ctrl-zoom-out"
              type="button"
              disabled={viewport.zoom - 1 < minZoom}
              title="Zoom Out"
            >
              <MinusIcon width={12} height={12} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapDispatchToProps = {
  setActiveRegion,
};

const mapStateToProps = (state) => ({
  qldCouncilsGeo: selectors.getQldCouncilsGeo(state),
  regions: selectors.getRegions(state),
  dams: selectors.getDamsByRegion(state),
  isMobile: selectors.getIsMobile(state),
});

Map.propTypes = {
  qldCouncilsGeo: PropTypes.object,
  onCouncilSelect: PropTypes.func,
  onDamSelect: PropTypes.func,
  regions: PropTypes.arrayOf(PropTypes.object),
  dams: PropTypes.arrayOf(PropTypes.object),
};

export default connect(mapStateToProps, mapDispatchToProps)(Map);



// WEBPACK FOOTER //
// ./src/components/map/map.js